import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import config from '@/config'

const recaptchaScriptId = 'nibiru-register-recaptcha-script'

interface RecaptchaFieldProps {
	name?: string
	title?: string
	resetTrigger?: unknown
	containerClassName?: string
	wrapperClassName?: string
	errorClassName?: string
}

const RecaptchaField = ({
	name = 'recaptchaToken',
	title = 'reCAPTCHA',
	resetTrigger,
	containerClassName = 'rounded-[1rem] border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 p-4',
	wrapperClassName = 'mt-4 w-full xl:mt-0 xl:max-w-[22rem]',
	errorClassName = 'mt-3 text-sm text-red-400',
}: RecaptchaFieldProps) => {
	const {
		setValue,
		clearErrors,
		setError,
		formState: { errors },
	} = useFormContext()
	const widgetContainerRef = useRef<HTMLDivElement | null>(null)
	const widgetIdRef = useRef<number | null>(null)
	const siteKey = config.RECAPTCHA_SITE_KEY?.trim()
	const fieldError = errors[name]

	useEffect(() => {
		if (!resetTrigger) {
			return
		}

		setValue(name, '', { shouldDirty: true, shouldValidate: false })
		clearErrors(name)
		window.grecaptcha?.reset(widgetIdRef.current ?? undefined)
	}, [clearErrors, name, resetTrigger, setValue])

	useEffect(() => {
		if (!siteKey) {
			setValue(name, '', { shouldValidate: true })
			setError(name, { type: 'manual', message: 'reCAPTCHA site key is not configured.' })
			return
		}

		let isMounted = true
		const previousOnLoad = window.onNibiruRegisterRecaptchaLoad

		const applyToken = (token: string, message?: string) => {
			setValue(name, token, { shouldDirty: true, shouldValidate: true })
			if (message) {
				setError(name, { type: 'manual', message })
				return
			}

			clearErrors(name)
		}

		const renderWidget = () => {
			if (!isMounted || !widgetContainerRef.current || !window.grecaptcha || widgetIdRef.current !== null) {
				return
			}

			widgetIdRef.current = window.grecaptcha.render(widgetContainerRef.current, {
				sitekey: siteKey,
				callback: (token: string) => applyToken(token),
				'expired-callback': () => {
					applyToken('', 'reCAPTCHA expired. Please verify again.')
					window.grecaptcha?.reset(widgetIdRef.current ?? undefined)
				},
				'error-callback': () => applyToken('', 'reCAPTCHA could not be loaded. Please refresh and try again.'),
			})
		}

		if (window.grecaptcha) {
			renderWidget()
		} else {
			window.onNibiruRegisterRecaptchaLoad = () => {
				previousOnLoad?.()
				renderWidget()
			}

			let script = document.getElementById(recaptchaScriptId) as HTMLScriptElement | null
			if (!script) {
				script = document.createElement('script')
				script.id = recaptchaScriptId
				script.src = 'https://www.google.com/recaptcha/api.js?onload=onNibiruRegisterRecaptchaLoad&render=explicit'
				script.async = true
				script.defer = true
				script.onerror = () => {
					if (isMounted) {
						applyToken('', 'reCAPTCHA could not be loaded. Please refresh and try again.')
					}
				}

				document.body.appendChild(script)
			}
		}

		return () => {
			isMounted = false
			window.onNibiruRegisterRecaptchaLoad = previousOnLoad
		}
	}, [clearErrors, name, setError, setValue, siteKey])

	return (
		<div className={wrapperClassName}>
			<p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">{title}</p>
			<div className={containerClassName}>
				<div ref={widgetContainerRef} />
				{fieldError && <p className={errorClassName}>{String(fieldError.message ?? '')}</p>}
			</div>
		</div>
	)
}

export default RecaptchaField
